import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { type CSSProperties, memo, useId, useMemo } from "react";
import { ASSISTANT_AVATAR } from "../../../theme/tokens";
import { type AssistantAvatarConfig, AssistantAvatarKind, AssistantAvatarMood } from "./assistantAvatarConfig";
import { type AssistantAvatarPointSide, type GazeDirection, gazeOffset, VIEW_BOX } from "./assistantAvatarGeometry";
import { AVATAR_PART, avatarGuideSx, avatarMotionSx, GAZE_VAR } from "./assistantAvatarMotion";
import { resolveBodyPalette } from "./assistantAvatarPalette";
import { CharacterFigure } from "./parts/character/CharacterFigure";
import { HumanFigure } from "./parts/human/HumanFigure";
import { AvatarCountBadge } from "./parts/shared/AvatarCountBadge";
import { AvatarSparkles } from "./parts/shared/AvatarSparkles";
import { AvatarThinkingDots } from "./parts/shared/AvatarThinkingDots";

export interface AssistantAvatarProps {
	config: AssistantAvatarConfig;
	mood: AssistantAvatarMood;
	/** Rendered width and height in px. */
	size: number;
	/** `false` renders the mood's still pose with no motion at all (e.g. every reply but the newest). */
	animated: boolean;
	/** Count dot at the top right, shown only when above 0 (e.g. approvals waiting). */
	badgeCount?: number;
	/** Screen-space direction the pupils look toward, e.g. from `gazeToward`. */
	// DEFAULT: absent keeps the mood's own pupil pose; every non-tour surface omits it.
	gaze?: GazeDirection;
	/** Screen side a raised arm points toward; replaces the needs-you wave while set. */
	// DEFAULT: absent draws no pointing arm (every non-tour surface).
	point?: AssistantAvatarPointSide;
	dataTestId: string;
}

type GazeStyle = CSSProperties & Record<(typeof GAZE_VAR)[keyof typeof GAZE_VAR], string>;

const gazeStyle = (gaze: GazeDirection): GazeStyle => {
	const offset = gazeOffset(gaze);
	return { [GAZE_VAR.X]: `${offset.x}px`, [GAZE_VAR.Y]: `${offset.y}px` };
};

// React's ids contain characters (`:`, `«»`) that break a `url(#…)` reference, so keep only safe ones.
const UNSAFE_ID_CHARS = /[^\w-]/g;

const sameConfig = (a: AssistantAvatarConfig, b: AssistantAvatarConfig): boolean => {
	if (a === b) return true;
	const bValues: Record<string, unknown> = { ...b };
	const aEntries = Object.entries(a);
	return aEntries.length === Object.keys(bValues).length && aEntries.every(([key, value]) => bValues[key] === value);
};

const sameGaze = (a: GazeDirection | undefined, b: GazeDirection | undefined): boolean =>
	a === b || (a !== undefined && b !== undefined && a.x === b.x && a.y === b.y);

const sameProps = (prev: AssistantAvatarProps, next: AssistantAvatarProps): boolean =>
	prev.mood === next.mood &&
	prev.size === next.size &&
	prev.animated === next.animated &&
	prev.badgeCount === next.badgeCount &&
	prev.point === next.point &&
	sameGaze(prev.gaze, next.gaze) &&
	prev.dataTestId === next.dataTestId &&
	sameConfig(prev.config, next.config);

/** A user-designed assistant avatar that shows what the assistant is doing through its mood. Decorative (`aria-hidden`). */
const AssistantAvatarComponent = ({ config, mood, size, animated, badgeCount, gaze, point, dataTestId }: AssistantAvatarProps) => {
	const theme = useTheme();
	const idPrefix = `assistant-avatar-${useId().replace(UNSAFE_ID_CHARS, "")}`;
	const body = resolveBodyPalette(config.color, theme.palette.primary);
	const gazing = gaze !== undefined;
	const pointing = point !== undefined;
	const rootSx = useMemo(
		() => [
			{ display: "block", overflow: "visible", width: size, height: size, flexShrink: 0 },
			...avatarMotionSx(mood, animated),
			...avatarGuideSx(gazing, pointing, animated),
		],
		[size, mood, animated, gazing, pointing],
	);
	// Gaze rides on inline custom properties so a new target never regenerates the emotion class.
	const style = gaze === undefined ? undefined : gazeStyle(gaze);
	return (
		<Box
			component="svg"
			viewBox={VIEW_BOX}
			aria-hidden
			data-testid={dataTestId}
			data-kind={config.kind}
			data-mood={mood}
			data-point={point}
			sx={rootSx}
			style={style}
		>
			<ellipse cx={60} cy={124} rx={30} ry={4.5} fill={ASSISTANT_AVATAR.EXTRAS.GROUND_SHADOW} />
			<g className={AVATAR_PART.BODY}>
				{config.kind === AssistantAvatarKind.Human ? (
					<HumanFigure config={config} mood={mood} body={body} idPrefix={idPrefix} size={size} point={point} />
				) : (
					<CharacterFigure config={config} mood={mood} body={body} idPrefix={idPrefix} point={point} />
				)}
			</g>
			{mood === AssistantAvatarMood.Thinking && <AvatarThinkingDots />}
			{/* Sparkles exist only as motion; a still done pose reads from the happy face alone. */}
			{mood === AssistantAvatarMood.Done && animated && <AvatarSparkles />}
			{/* DEFAULT: an absent badgeCount draws no badge, the same as 0. */}
			{badgeCount !== undefined && badgeCount > 0 && <AvatarCountBadge count={badgeCount} size={size} />}
		</Box>
	);
};

/** Memoised by value, so a parent re-rendering per streamed token (or rebuilding a preview config) doesn't redraw the SVG. */
export const AssistantAvatar = memo(AssistantAvatarComponent, sameProps);
